export const OTHER_ORGANISATION = 'Other';

// A person on one of these is not telling us where they work, so every one of them collapses into
// a single "Other" bucket rather than becoming an organisation named Gmail.
export const GENERIC_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com',
  'hotmail.com',
  'icloud.com',
  'outlook.com',
  'yahoo.com',
];

const GENERIC_DOMAINS = new Set(GENERIC_EMAIL_DOMAINS);

// Second-level labels that are part of the public suffix rather than the organisation, so
// `acme.co.uk` resolves to Acme and not to Co.
const PUBLIC_SECOND_LEVEL_LABELS = new Set(['ac', 'co', 'com', 'edu', 'gov', 'net', 'org']);

const titleCase = (label: string): string =>
  label
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const domainOfEmail = (email: string): null | string => {
  const at = email.lastIndexOf('@');
  if (at < 0) return null;

  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return domain.includes('.') ? domain : null;
};

/**
 * The rule proper, over a domain rather than an address.
 *
 * Split out so the Visitors tab can filter by organisation in SQL without a second definition of
 * the rule: the query hands back the distinct domains it holds, this maps each to a name, and the
 * chosen names translate back into the domains that produced them. One rule, two directions.
 */
export const organisationFromDomain = (domain: null | string | undefined): string => {
  if (!domain || GENERIC_DOMAINS.has(domain)) return OTHER_ORGANISATION;
  if (!domain.includes('.')) return OTHER_ORGANISATION;

  const labels = domain.split('.').filter(Boolean);
  if (labels.length < 2) return OTHER_ORGANISATION;

  const secondToLast = labels[labels.length - 2];
  const organisationLabel =
    labels.length > 2 && PUBLIC_SECOND_LEVEL_LABELS.has(secondToLast)
      ? labels[labels.length - 3]
      : secondToLast;

  const name = titleCase(organisationLabel);
  return name || OTHER_ORGANISATION;
};

/**
 * Organisation is derived, never stored: the story asks for a grouping an Admin recognises, and
 * the only signal we have is the email domain. Anything we cannot resolve — a generic provider, a
 * malformed address, a bare hostname — is "Other" rather than a guess.
 */
export const organisationFromEmail = (email: null | string | undefined): string =>
  organisationFromDomain(domainOfEmail(email ?? ''));
